import { useState } from "react";
import { ChartBar, Package, ChatText } from "@phosphor-icons/react";
import { inp, todayWIB } from "../components/reporting/util";
import { Card, useLoad } from "../components/reporting/v2/common";

const th = "px-3 py-2 text-left text-[10px] uppercase tracking-widest text-neutral-500 font-medium whitespace-nowrap";
const td = "px-3 py-2 text-sm whitespace-nowrap";
const num = `${td} text-right tabular-nums`;
const dash = (v, f = (x) => x) => (v === null || v === undefined ? "—" : f(v));
const pct = (v) => `${v}%`;
const lbl = "mt-2 text-[10px] uppercase tracking-widest text-neutral-400";

function Filters({ f, setF, admins }) {
    const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
    return (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="meta-filters">
            <label className="text-xs text-neutral-500">Dari<input type="date" value={f.date_from} onChange={set("date_from")} className={inp} data-testid="meta-date-from" /></label>
            <label className="text-xs text-neutral-500">Sampai<input type="date" value={f.date_to} onChange={set("date_to")} className={inp} data-testid="meta-date-to" /></label>
            <label className="text-xs text-neutral-500">Sales Admin
                <select value={f.admin_id} onChange={set("admin_id")} className={inp} data-testid="meta-admin-filter">
                    <option value="">Semua admin</option>
                    {(admins || []).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
            </label>
        </div>
    );
}

function Table({ head, children, testid, empty }) {
    return (
        <div className="overflow-x-auto -mx-4 px-4" data-testid={testid}>
            <table className="min-w-full border-collapse">
                <thead className="border-b border-mist"><tr>{head.map(([l, right]) => <th key={l} className={`${th} ${right ? "text-right" : ""}`}>{l}</th>)}</tr></thead>
                <tbody className="divide-y divide-mist/70">{children}</tbody>
            </table>
            {empty && <p className="text-sm text-neutral-400 py-4" data-testid={`${testid}-empty`}>{empty}</p>}
        </div>
    );
}

function AdminPerformance({ data }) {
    if (!data) return <p className="text-sm text-neutral-400">Memuat…</p>;
    const t = data.total;
    return (
        <Card title="Performa Meta Ads per Admin" testid="meta-admin-card">
            <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-4" data-testid="meta-totals">
                {[["Ads Leads", t.leads, "leads"], ["Closing", dash(t.closings), "closings"], ["Konversi", dash(t.conversion_rate, pct), "rate"]].map(([l, v, k]) => (
                    <div key={k} className="border border-mist rounded-sm p-3"><div className="text-[10px] uppercase tracking-widest text-neutral-500">{l}</div><div className="text-xl font-serif tabular-nums" data-testid={`meta-total-${k}`}>{v}</div></div>
                ))}
            </div>
            <Table testid="meta-admin-table" empty={data.rows.length ? null : "Belum ada Report PIC terkirim pada periode ini."}
                head={[["Admin"], ["Laporan", 1], ["Ads Leads", 1], ["Closing", 1], ["Konversi", 1]]}>
                {data.rows.map((r) => (
                    <tr key={r.admin_id} data-testid={`meta-admin-row-${r.admin_id}`}>
                        <td className={td}>{r.admin_name}</td><td className={num}>{r.reports}</td><td className={num}>{r.leads}</td><td className={num}>{dash(r.closings)}</td>
                        <td className={num}>{dash(r.conversion_rate, pct)}</td>
                    </tr>
                ))}
            </Table>
            <p className="text-[11px] text-neutral-400 mt-3">Sumber: baris Ads/produk di Report PIC yang dikirim admin (bukan Total Chat Handled). Konversi = Closing ÷ Ads Leads pada baris Ads yang mencatat Closing. Nilai penjualan per Ads tidak dicatat di Report PIC.</p>
        </Card>
    );
}

function Products({ data }) {
    if (!data) return null;
    return (
        <Card title={<span className="inline-flex items-center gap-2"><Package size={16} /> Performa Produk Ads</span>} testid="meta-products-card">
            <Table testid="meta-products-table" empty={data.products.length ? null : "Belum ada produk Ads pada periode ini."}
                head={[["Produk"], ["Ads Leads", 1], ["Closing", 1], ["Konversi", 1], ["Alasan belum closing"]]}>
                {data.products.map((p) => (
                    <tr key={p.product_name} data-testid={`meta-product-row-${p.product_name}`}>
                        <td className={`${td} whitespace-normal min-w-[10rem]`}>{p.product_name}</td><td className={num}>{p.leads}</td><td className={num}>{p.leads_with_closing ? p.closings : "—"}</td>
                        <td className={num}>{dash(p.conversion_rate, pct)}</td>
                        <td className={`${td} whitespace-normal min-w-[14rem] text-xs text-neutral-600`}>{Object.entries(p.reasons).map(([k, n]) => `${k} (${n})`).join(", ") || "—"}</td>
                    </tr>
                ))}
            </Table>
        </Card>
    );
}

function Report({ r }) {
    return (
        <div className="border-b border-mist pb-3 text-sm" data-testid={`meta-report-${r.admin_id}-${r.reporting_date}`}>
            <div className="font-medium">{r.reporting_date} · {r.admin_name}</div>
            <div className={lbl}>Ads Analysis</div>
            {r.no_ads ? <p className="text-xs text-neutral-500">Tidak ada aktivitas Ads.</p> : r.ads.length === 0 ? <p className="text-xs text-neutral-600" data-testid="meta-report-ads-note">{r.ads_note || "—"}</p> : r.ads.map((x, i) => (
                <div key={i} className="text-xs mt-1"><b>{x.product_name}</b> · {x.leads} Ads Leads · {dash(x.closings)} closing{x.reasons.length > 0 && ` · ${x.reasons.join(", ")}`}{x.explanation && <div className="text-neutral-600">{x.explanation}</div>}</div>
            ))}
            {r.customer_insight && <><div className={lbl}>Customer Insight</div><p className="text-xs text-neutral-700" data-testid="meta-report-insight">{r.customer_insight}</p></>}
            {r.issues.length > 0 && <div className={lbl}>Kendala & Improvement</div>}
            {r.issues.map((x, i) => <div key={i} className="text-xs mt-1" data-testid="meta-report-issue"><b>{x.category}</b>: {x.description} → <i>{x.action}</i></div>)}
        </div>
    );
}

function Analysis({ data }) {
    if (!data) return null;
    return (
        <Card title={<span className="inline-flex items-center gap-2"><ChatText size={16} /> Insight & Analisis Admin</span>} testid="meta-analysis-card">
            <div className="space-y-3" data-testid="meta-analysis-list">
                {data.reports.length === 0 && <p className="text-sm text-neutral-400" data-testid="meta-analysis-empty">Belum ada analisis pada periode ini.</p>}
                {data.reports.map((r) => <Report key={`${r.admin_id}-${r.reporting_date}`} r={r} />)}
            </div>
        </Card>
    );
}

export default function MetaAds({ embedded = false }) {
    const today = todayWIB();
    const [f, setF] = useState({ date_from: `${today.slice(0, 8)}01`, date_to: today, admin_id: "" });
    const params = { date_from: f.date_from, date_to: f.date_to, ...(f.admin_id ? { admin_id: f.admin_id } : {}) };
    const [admins] = useLoad("/reporting/meta-ads/admins");
    const [summary] = useLoad("/reporting/meta-ads/summary", params);
    const [products] = useLoad("/reporting/meta-ads/products", params);
    const [analysis] = useLoad("/reporting/meta-ads/analysis", params);
    return (
        <div className={`${embedded ? "" : "reporting-ui pb-10"} space-y-5`} data-testid="meta-ads-page">
            {!embedded && <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-400">H&T Group · Read-only</div>
                <h1 className="font-serif text-3xl md:text-4xl tracking-tight inline-flex items-center gap-3"><ChartBar size={28} /> Meta Ads</h1>
            </div>}
            <Filters f={f} setF={setF} admins={admins} />
            <AdminPerformance data={summary} />
            <Products data={products} />
            <Analysis data={analysis} />
        </div>
    );
}
